import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    FormBuilder,
    FormGroup,
    Validators,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import {
    ReportCategoryService,
    CreateReportCategoryRequest,
} from '../../../services/report-category.service';
import { PowerBIService, ReportInfo } from '../../../services/powerbi.service';
import { LoggerService } from '../../../core/services/logger.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
    selector: 'app-create-report-category-page',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatSnackBarModule,
        MatCardModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './create-report-category-page.component.html',
    styleUrl: './create-report-category-page.component.scss',
})
export class CreateReportCategoryPageComponent implements OnInit {
    form: FormGroup;
    isLoading = false;
    isLoadingReports = false;
    availableReports: ReportInfo[] = [];

    categories = [
        { value: 'operativo', label: 'Operativo' },
        { value: 'financiero', label: 'Financiero' },
        { value: 'gerencial', label: 'Gerencial' },
    ];

    constructor(
        private fb: FormBuilder,
        private reportCategoryService: ReportCategoryService,
        private powerBIService: PowerBIService,
        private snackBar: MatSnackBar,
        private logger: LoggerService,
        private router: Router
    ) {
        this.form = this.fb.group({
            reportId: ['', [Validators.required]],
            category: ['', [Validators.required]],
        });
    }

    ngOnInit(): void {
        this.loadReports();
    }

    private loadReports(): void {
        this.isLoadingReports = true;
        this.powerBIService
            .getAllReports()
            .pipe(
                catchError((err) => {
                    this.logger.error(
                        'Error al cargar reportes',
                        err as Error,
                        'CreateReportCategoryPageComponent'
                    );
                    this.snackBar.open(
                        'Error al cargar reportes de Power BI',
                        'Cerrar',
                        { duration: 5000, panelClass: ['error-snackbar'] }
                    );
                    return of({ success: false, data: [], timestamp: '' });
                }),
                finalize(() => {
                    this.isLoadingReports = false;
                })
            )
            .subscribe((res) => {
                if (res.success && res.data) {
                    this.availableReports = res.data;
                }
            });
    }

    getError(field: string): string {
        const c = this.form.get(field);
        if (!c) return '';
        if (c.hasError('required')) return 'Este campo es requerido';
        return 'Campo inválido';
    }

    markTouched(): void {
        Object.keys(this.form.controls).forEach((k) =>
            this.form.get(k)?.markAsTouched()
        );
    }

    onSubmit(): void {
        if (!this.form.valid) {
            this.markTouched();
            return;
        }
        this.isLoading = true;
        const v = this.form.value;

        // Buscar nombre del reporte seleccionado
        const selectedReport = this.availableReports.find(
            (r) => r.id === v.reportId
        );

        const body: CreateReportCategoryRequest = {
            reportId: v.reportId,
            category: v.category,
            reportName: selectedReport?.name,
        };

        this.reportCategoryService
            .createMapping(body)
            .pipe(
                catchError((err) => {
                    this.logger.error(
                        'Error al asignar categoría',
                        err as Error,
                        'CreateReportCategoryPageComponent'
                    );
                    const msg =
                        err.error?.message || 'Error al asignar la categoría';
                    this.snackBar.open(msg, 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar'],
                    });
                    return of(null);
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((res) => {
                if (res?.success) {
                    this.snackBar.open(
                        'Reporte asignado a categoría exitosamente',
                        'Cerrar',
                        { duration: 3000, panelClass: ['success-snackbar'] }
                    );
                    this.router.navigate(['/modules/report-categories']);
                }
            });
    }

    onCancel(): void {
        this.router.navigate(['/modules/report-categories']);
    }
}
